/**
 * Getting a clinic's logo ready to upload, and showing it once it is there.
 *
 * ## Why the browser resizes before uploading
 *
 * People upload what they have: a 4000px export from a designer, a phone photo
 * of a business card. The logo is shown at about 34px in the sidebar. Resizing
 * to 512px on the way out turns megabytes into a few kilobytes, keeps the API's
 * 256 KB cap from ever being the thing a clinic owner argues with, and — as a
 * side effect — re-encodes the image through a canvas, which drops metadata
 * (camera location included) and anything that is not pixels.
 */
import { useEffect, useState } from 'react';
import { LOGO_MAX_BYTES, LOGO_MAX_EDGE_PX } from '@inventory-tracker/core';
import { fetchLogo } from '../lib/api.js';

/** Scale to fit inside `max`×`max`, keeping proportions. Never scales up. */
export function fitWithin(
  width: number,
  height: number,
  max: number = LOGO_MAX_EDGE_PX,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export interface PreparedLogo {
  contentType: 'image/png' | 'image/webp';
  dataBase64: string;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.onload = () => {
      // "data:image/png;base64,AAAA" — everything after the comma.
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Decode, resize and re-encode. PNG first, because logos usually have
 * transparency and hard edges; WebP as a fallback when a photographic logo is
 * still over the cap as a PNG. Throws a message fit to show a clinic owner.
 */
export async function prepareLogo(file: File): Promise<PreparedLogo> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('That file is not an image we can read. Try a PNG or JPEG.');
  }

  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx || width === 0) throw new Error('That image could not be processed.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const png = await toBlob(canvas, 'image/png');
  if (png && png.size <= LOGO_MAX_BYTES) {
    return { contentType: 'image/png', dataBase64: await blobToBase64(png) };
  }

  const webp = await toBlob(canvas, 'image/webp', 0.85);
  // Safari before 16 ignores the type and hands back a PNG; check what came out.
  if (webp && webp.type === 'image/webp' && webp.size <= LOGO_MAX_BYTES) {
    return { contentType: 'image/webp', dataBase64: await blobToBase64(webp) };
  }

  throw new Error('That image is too detailed to use as a logo. Try a simpler or smaller one.');
}

/**
 * An object URL for the clinic's logo, or null when there is none (or it could
 * not be loaded — the monogram is a perfectly good fallback, so a failed logo
 * is never an error on screen).
 */
export function useLogoUrl(logoVersion: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoVersion) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    fetchLogo(logoVersion)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [logoVersion]);

  return url;
}
